import { NotAnswerInquiryDto } from "interface/admin/interface.inquiry";
import {
  CompleteTxt,
  Pendding,
  TableContainer,
  TableContents,
  TableTop,
} from "style/admin/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { formatDateTime } from "utils/util";
import PaginationControls from "../table.components/pagination";
import { useNavigate } from "react-router-dom";

interface DataProps {
  data: NotAnswerInquiryDto[];
}

const InquiryTable = ({ data }: DataProps) => {
  const navigate = useNavigate();
  // ✅ 최신 등록일(`createdAt`)을 기준으로 내림차순 정렬
  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(b.regDt).getTime() - new Date(a.regDt).getTime()
    );
  }, [data]);

  const columns = useMemo<ColumnDef<NotAnswerInquiryDto>[]>(
    () => [
      {
        header: "Index",
        id: "number", // 넘버링을 위한 id
        cell: ({ row }) => sortedData.length - row.index, // 각 행의 인덱스에 1을 더해 넘버링
        size: 120,
      },
      {
        header: "제목",
        accessorKey: "title",
        cell: (info) => {
          const value = info.getValue();
          return (
            <div
              className="title-box"
              onClick={() => clickedRowHandler(info.row.original.inquiryId)}
            >
              {String(value)}
            </div>
          );
        },
        size: 318,
      },
      {
        header: "작성자",
        accessorKey: "userName",
        cell: (info) => {
          const row = info.row.original; // ✅ 현재 행(row)의 원본 데이터 가져오기
          const userName = row.userName;
          const userProfileUrl =
            row.userProfileUrl || "/assets/icon/userprofile.png"; // 기본 이미지 설정

          return userName ? (
            <div className="profile">
              <div className="img-box">
                <img
                  src={
                    userProfileUrl
                      ? userProfileUrl
                      : "/assets/icon/userprofile.png"
                  }
                  alt={`${userName} 프로필`}
                />
              </div>
              <span>{userName}</span> {/* ✅ 텍스트 올바르게 출력 */}
            </div>
          ) : (
            "-"
          );
        },
        size: 160,
      },
      {
        header: "작성일시",
        accessorKey: "regDt",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value ? formatDateTime(String(value)) : "-"; // ✅ 값이 없으면 "-" 반환
        },
        size: 160,
      },
      {
        header: "답변상태",
        accessorKey: "answerYn",
        cell: (info) =>
          info.getValue() === "N" ? (
            <Pendding>
              <span>답변대기</span>
            </Pendding>
          ) : (
            <CompleteTxt>답변완료</CompleteTxt>
          ),
        size: 96,
      },
      {
        header: "답변자",
        accessorKey: "answerRegUserId",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 160,
      },
      {
        header: "답변일시",
        accessorKey: "answerDt",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value ? formatDateTime(String(value)) : "-"; // ✅ 값이 없으면 "-" 반환
        },
        size: 160,
      },
    ],
    []
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } }, // ✅ 초기 페이지 크기 설정
  });

  const paginationState = table.getState().pagination;
  const startRow = paginationState.pageIndex * paginationState.pageSize + 1;
  const endRow = startRow + table.getRowModel().rows.length - 1;
  const totalRows = sortedData.length;

  const clickedRowHandler = (id: number) => {
    navigate(`/admin/inquiry/detail/${id}`);
  };

  return (
    <TableContainer>
      <TableTop>
        <span className="table-count">
          {totalRows} 중 {startRow}-{endRow}
        </span>
        <PaginationControls table={table} />
      </TableTop>
      <TableContents>
        <table
          className={
            table.getRowModel().rows.length === 0 ? "no-data-table" : ""
          }
        >
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ minWidth: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onDoubleClick={() =>
                    clickedRowHandler(row.original.inquiryId)
                  } // ✅ 더블 클릭 시 상세 페이지 이동
                  style={{ cursor: "pointer" }} // ✅ 마우스 오버 시 클릭 가능하도록 스타일 추가
                >
                  {row.getVisibleCells().map((cell) => {
                    const isPendingAnswer =
                      cell.column.id === "answerYn" && cell.getValue() === "N";
                    const isTitle = cell.column.id === "title";
                    const isProfile = cell.column.id === "userName";

                    return (
                      <td
                        key={cell.id}
                        className={`${
                          isPendingAnswer ? "pending-answer" : ""
                        } ${isTitle ? "title-cell" : ""} ${
                          isProfile ? "profile-cell" : ""
                        }`.trim()} // ✅ 여러 개의 클래스 추가
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              // ✅ 데이터가 없을 경우 "검색 결과가 없습니다" 표시
              <tr className="no-data">
                <td colSpan={columns.length} className="no-data-td">
                  검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContents>
    </TableContainer>
  );
};

export default InquiryTable;
